import React from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';

export function getVisiblePages(currentPage, totalPages, siblingCount = 1) {
    if (totalPages <= 1) {
        return totalPages === 1 ? [1] : [];
    }

    const totalNumbers = siblingCount * 2 + 5;
    if (totalPages <= totalNumbers) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSibling = Math.max(currentPage - siblingCount, 1);
    const rightSibling = Math.min(currentPage + siblingCount, totalPages);
    const showLeftEllipsis = leftSibling > 2;
    const showRightEllipsis = rightSibling < totalPages - 1;

    if (!showLeftEllipsis && showRightEllipsis) {
        const leftRange = Array.from({ length: 3 + siblingCount * 2 }, (_, i) => i + 1);
        return [...leftRange, 'ellipsis-right', totalPages];
    }

    if (showLeftEllipsis && !showRightEllipsis) {
        const rightRange = Array.from(
            { length: 3 + siblingCount * 2 },
            (_, i) => totalPages - (2 + siblingCount * 2) + i
        );
        return [1, 'ellipsis-left', ...rightRange];
    }

    const middleRange = Array.from(
        { length: rightSibling - leftSibling + 1 },
        (_, i) => leftSibling + i
    );
    return [1, 'ellipsis-left', ...middleRange, 'ellipsis-right', totalPages];
}

export default function PagePagination({
    currentPage,
    totalPages,
    onPageChange,
    ariaLabel = 'Pages',
    showMeta = true,
}) {
    if (totalPages <= 1) {
        return null;
    }

    const pages = getVisiblePages(Number(currentPage), Number(totalPages));

    const goTo = (page) => {
        if (page < 1 || page > totalPages || page === currentPage) {
            return;
        }
        onPageChange(page);
    };

    return (
        <div className="gs-pagination">
            {showMeta && (
                <p className="gs-pagination__meta" aria-live="polite">
                    Page {currentPage} of {totalPages}
                </p>
            )}
            <Pagination aria-label={ariaLabel} className="gs-pagination__list">
                <PaginationItem disabled={currentPage <= 1}>
                    <PaginationLink
                        tag="button"
                        type="button"
                        previous
                        onClick={() => goTo(currentPage - 1)}
                    />
                </PaginationItem>
                {pages.map((page) => {
                    if (typeof page === 'string') {
                        return (
                            <PaginationItem
                                key={page}
                                disabled
                                className="gs-pagination__ellipsis"
                            >
                                <PaginationLink tag="button" type="button" tabIndex={-1}>
                                    …
                                </PaginationLink>
                            </PaginationItem>
                        );
                    }
                    return (
                        <PaginationItem active={page === currentPage} key={page}>
                            <PaginationLink
                                tag="button"
                                type="button"
                                onClick={() => goTo(page)}
                                aria-current={page === currentPage ? 'page' : undefined}
                            >
                                {page}
                            </PaginationLink>
                        </PaginationItem>
                    );
                })}
                <PaginationItem disabled={currentPage >= totalPages}>
                    <PaginationLink
                        tag="button"
                        type="button"
                        next
                        onClick={() => goTo(currentPage + 1)}
                    />
                </PaginationItem>
            </Pagination>
        </div>
    );
}
